/**
 * Expanded inventory item view, redesigned as an inspector-style detail
 * sheet: identity header with actions, key-facts strip, media rail,
 * aligned detail sections (Product / Logistics / Operations), status
 * flags, notes callout, and a tabbed activity region. Field and action
 * parity with reware-admin's Inventory Items card.
 */
import { useNotify, useRecordContext } from 'react-admin';
import {
  Box,
  Chip,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';

import { displayNameFor } from './format';
import ItemActions from './ItemActions';
import ItemActivityTabs from './ItemActivityTabs';
import {
  KeyFacts,
  LogisticsSection,
  OperationsSection,
  ProductSection,
  StatusFlags,
} from './ItemDetailSections';
import ItemMediaGallery from './ItemMediaGallery';
import type { ConstantOption, InventoryItemRecord } from './types';

const CopyButton = ({ name, value }: { name: string, value: string }) => {
  const notify = useNotify();
  return (
    <IconButton
      size="small"
      aria-label={`Copy ${name}`}
      sx={{ p: 0.25, ml: 0.25 }}
      onClick={async () => {
        // Report success only if the copy actually happened — the Clipboard API
        // is absent on insecure origins and writeText can reject.
        try {
          if (!navigator.clipboard) throw new Error('Clipboard unavailable');
          await navigator.clipboard.writeText(value);
          notify(`${name} copied`, { type: 'info' });
        } catch {
          notify(`Couldn't copy ${name}`, { type: 'warning' });
        }
      }}
    >
      <ContentCopyIcon sx={{ fontSize: 13 }} />
    </IconButton>
  );
};

type StateColor = 'success' | 'error' | 'warning' | 'info' | 'default';

const stateColor = (state: string): StateColor => {
  if (/REJECTED|DAMAGED|MISSING|SHRINK|LOST|PROBLEM|WRONG/.test(state)) return 'error';
  if (/APPROVED|CURATED$/.test(state)) return 'success';
  if (/ORDERED|PICK|PACK|SHIPPED|DROPPED/.test(state)) return 'info';
  if (/PENDING|NEEDS|REVIEW|IN_PROGRESS|HOLD|CLEANING|REPAIR|PROCESSING/.test(state)) return 'warning';
  return 'default';
};

interface InventoryItemExpandProps {
  states: ConstantOption[];
  conditions: ConstantOption[];
  rejectReasons: ConstantOption[];
}

export const InventoryItemExpand = ({ states, conditions, rejectReasons }: InventoryItemExpandProps) => {
  const record = useRecordContext<InventoryItemRecord>();
  if (!record) return null;

  const shopifyUrl = record.productVariant?.subdomain && record.productVariant?.handle ?
    `https://${record.productVariant.subdomain}/products/${record.productVariant.handle}` :
    null;
  const externals = (record.inventoryItemExternals ?? []).filter(external => external.externalUrl);
  const recommended = record.recommendedCondition ?
    `${displayNameFor(conditions, record.recommendedCondition)}${record.catalogItem?.rubricCode ? ` (${record.catalogItem.rubricCode})` : ''}` :
    null;

  return (
    <Box sx={{ bgcolor: 'background.default', borderTop: '1px solid', borderColor: 'divider', p: 2.5 }}>
      <Stack spacing={2}>
        {/* Identity header + actions */}
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'flex-start' }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontSize: 16, lineHeight: 1.3 }}>
                {record.catalogItem?.title ?? `Item ${record.id}`}
              </Typography>
              <Chip size="small" color={stateColor(record.state)} label={displayNameFor(states, record.state)} />
              {recommended && (
                <Chip size="small" variant="outlined" label={`Recommended: ${recommended}`} />
              )}
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              useFlexGap
              sx={{ flexWrap: 'wrap', mt: 0.5, color: 'text.secondary' }}
            >
              <Typography variant="caption">
                ID
                {' '}
                {record.id}
                <CopyButton name="Item ID" value={String(record.id)} />
              </Typography>
              <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                {`inv-${record.uuid}`}
                <CopyButton name="Item UUID" value={`inv-${record.uuid}`} />
              </Typography>
              {shopifyUrl && (
                <Link href={shopifyUrl} target="_blank" rel="noopener" variant="caption" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  Shopify Product
                  {' '}
                  {record.productVariant?.productId}
                  <OpenInNewIcon sx={{ fontSize: 12, ml: 0.25 }} />
                </Link>
              )}
              {externals.map(external => (
                <Link
                  key={`${external.externalSystem}-${external.externalIdentifier}`}
                  href={external.externalUrl ?? '#'}
                  target="_blank"
                  rel="noopener"
                  variant="caption"
                  sx={{ display: 'inline-flex', alignItems: 'center' }}
                >
                  {external.externalSystem ?? 'External'}
                  {' '}
                  {external.externalIdentifier}
                  <OpenInNewIcon sx={{ fontSize: 12, ml: 0.25 }} />
                </Link>
              ))}
            </Stack>
          </Box>
          <ItemActions record={record} conditions={conditions} rejectReasons={rejectReasons} />
        </Stack>

        <StatusFlags record={record} rejectReasons={rejectReasons} />

        {/* Notes callout */}
        {record.notes && (
          <Stack
            direction="row"
            spacing={1}
            alignItems="flex-start"
            sx={{
              bgcolor: '#FDF6EC',
              border: '1px solid',
              borderColor: '#F0DFC8',
              borderRadius: 1,
              px: 1.5,
              py: 1,
            }}
          >
            <StickyNote2OutlinedIcon sx={{ fontSize: 16, mt: '2px', color: '#9A6A2F' }} />
            <Typography variant="body2" sx={{ color: '#6B4F26' }}>
              {record.notes}
            </Typography>
          </Stack>
        )}

        <KeyFacts record={record} conditions={conditions} />

        {/* Media rail + detail sections */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <ItemMediaGallery record={record} />
          <Box
            sx={{
              flexGrow: 1,
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(250px, 1fr))' },
              columnGap: 4,
              rowGap: 2.5,
              alignContent: 'start',
            }}
          >
            <ProductSection />
            <LogisticsSection />
            <OperationsSection />
          </Box>
        </Stack>

        {record.storefrontPhrase && (
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            “
            {record.storefrontPhrase}
            ”
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled' }}>
              storefront phrase
            </Typography>
          </Typography>
        )}

        <ItemActivityTabs record={record} />
      </Stack>
    </Box>
  );
};

export default InventoryItemExpand;
